import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FlaskConical, Loader2, Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, dataBR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { EmptyState, ErrorState, Field, Loading, PageHeader, Surface, Thumb } from "@/components/ds";
import { ImageUpload } from "@/components/ds/ImageUpload";
import { ResponsiveModal } from "@/components/ds/ResponsiveModal";
import { NativeSelect } from "@/components/ds/NativeSelect";
import { DEV_STATUS, devStatusCls } from "@/components/ds/devStatus";

const norm = (v) =>
  (v || "")
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const FILTERS = [
  ["TODOS", "Todos"],
  ["EM_DESENVOLVIMENTO", "Em desenvolvimento"],
  ["EM_TESTES", "Em testes"],
  ["AJUSTES", "Ajustes necessários"],
  ["APROVADO", "Aprovados"],
  ["TRANSFORMADO", "Transformados em produto"],
  ["DESATIVADO", "Desativados"],
];

export default function Development() {
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("TODOS");
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);
  const [cats, setCats] = useState([]);

  const load = useCallback(async () => {
    setError("");
    try {
      const [p, c] = await Promise.all([api.get("/dev-projects"), api.get("/categories")]);
      setList(p.data);
      setCats(c.data);
    } catch (e) {
      setError(apiError(e));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const visible = useMemo(() => {
    const rows = (list || []).filter((p) =>
      filter === "TODOS" ? p.status !== "DESATIVADO" : p.status === filter
    );
    if (!search.trim()) return rows;
    const q = norm(search);
    return rows.filter(
      (p) => norm(p.name).includes(q) || norm(p.code).includes(q) || norm(p.category).includes(q)
    );
  }, [list, filter, search]);

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (list === null) return <Loading label="Carregando projetos..." />;

  const count = (key) =>
    key === "TODOS"
      ? list.filter((p) => p.status !== "DESATIVADO").length
      : list.filter((p) => p.status === key).length;

  return (
    <div className="space-y-6" data-testid="development-page">
      <PageHeader
        title="Desenvolvimento de Produtos"
        subtitle="Desenvolva, teste e aperfeiçoe seus produtos antes de colocá-los em produção e venda."
        action={
          <Button
            onClick={() => setCreating(true)}
            data-testid="new-project-btn"
            className="h-11 gap-2 rounded-full px-5 font-semibold transition-transform active:scale-95"
          >
            <Plus className="h-4 w-4" /> Novo projeto
          </Button>
        }
      />

      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap gap-2">
          {FILTERS.map(([key, label]) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              data-testid={`dev-filter-${key.toLowerCase()}`}
              className={`tr-chip ${filter === key ? "tr-chip-active" : ""}`}
            >
              {label} <span className="ml-1 font-num opacity-60">{count(key)}</span>
            </button>
          ))}
        </div>
        <div className="relative w-full lg:max-w-xs">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nome, código ou categoria..."
            className="pl-9"
            data-testid="dev-search-input"
          />
        </div>
      </div>

      {visible.length === 0 ? (
        <EmptyState
          icon={FlaskConical}
          title="Nenhum projeto por aqui"
          description="Crie um projeto para desenvolver uma peça nova: teste, ajuste, calcule o custo e transforme em produto."
          action={
            <Button onClick={() => setCreating(true)} data-testid="empty-new-project-btn" className="gap-2 rounded-full">
              <Plus className="h-4 w-4" /> Novo projeto
            </Button>
          }
        />
      ) : (
        <div className="grid gap-4 stagger md:grid-cols-2 xl:grid-cols-3">
          {visible.map((p) => (
            <Surface
              key={p.id}
              onClick={() => navigate(`/desenvolvimento/${p.id}`)}
              data-testid={`project-card-${p.id}`}
              className="min-w-0 cursor-pointer overflow-hidden p-5 transition-transform hover:-translate-y-0.5"
            >
              <div className="flex items-start gap-3">
                <Thumb src={p.photo_path} alt={p.name} className="h-14 w-14 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-display text-base font-medium" data-testid={`project-name-${p.id}`}>
                    {p.name}
                  </p>
                  <p className="truncate text-[11px] text-muted-foreground">
                    {p.code} · {p.category || "Sem categoria"}
                  </p>
                </div>
                <span
                  className={`shrink-0 rounded-full border px-2.5 py-1 text-[11px] ${devStatusCls(p.status)}`}
                  data-testid={`project-status-${p.id}`}
                >
                  {DEV_STATUS[p.status] || p.status}
                </span>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
                <div>
                  <p className="text-muted-foreground">Testes</p>
                  <p className="font-num text-sm" data-testid={`project-tests-${p.id}`}>
                    {p.tests_count || 0}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Último teste</p>
                  <p className="text-sm">{p.last_test_at ? dataBR(p.last_test_at) : "—"}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Filamentos</p>
                  <p className="font-num text-sm">{(p.filaments || []).length}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Plates</p>
                  <p className="font-num text-sm">{(p.plates || []).length}</p>
                </div>
              </div>

              {p.last_result && (
                <p className="mt-3 truncate text-[11px] text-muted-foreground">
                  Último resultado: {DEV_STATUS.RESULTS[p.last_result] || p.last_result}
                </p>
              )}
              <p className="mt-2 text-[11px] text-muted-foreground">
                Criado em {dataBR(p.created_at)} · atualizado em {dataBR(p.updated_at)}
              </p>
            </Surface>
          ))}
        </div>
      )}

      {creating && (
        <ProjectModal
          cats={cats}
          onClose={() => setCreating(false)}
          onSaved={(doc) => navigate(`/desenvolvimento/${doc.id}`)}
        />
      )}
    </div>
  );
}

export function ProjectModal({ project = null, cats = [], onClose, onSaved }) {
  const [form, setForm] = useState({
    name: project?.name || "",
    category_id: project?.category_id || "",
    category: "",
    description: project?.description || "",
    notes: project?.notes || "",
    photo_path: project?.photo_path || null,
  });
  const [newCat, setNewCat] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = {
      name: form.name,
      category_id: newCat ? null : form.category_id || null,
      category: newCat ? form.category : "",
      description: form.description,
      notes: form.notes,
      photo_path: form.photo_path,
    };
    try {
      const { data } = project
        ? await api.put(`/dev-projects/${project.id}`, body)
        : await api.post("/dev-projects", body);
      toast.success(project ? "Projeto atualizado." : "Projeto criado.");
      onClose();
      onSaved(data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ResponsiveModal
      open
      onOpenChange={(o) => !o && onClose()}
      title={project ? "Editar projeto" : "Novo projeto"}
      description="O projeto é o centro do desenvolvimento: materiais, Plates, testes e custos ficam todos aqui."
      testid="project-modal"
    >
      <form onSubmit={submit} className="mt-2 flex flex-col gap-4">
        <ImageUpload
          value={form.photo_path}
          onChange={(p) => setForm((f) => ({ ...f, photo_path: p }))}
          testid="project-image"
        />
        <Field label="Nome do projeto">
          <Input
            value={form.name}
            onChange={set("name")}
            required
            placeholder="Ex.: Copo Monster"
            data-testid="project-name-input"
          />
        </Field>
        <Field label="Categoria">
          {newCat ? (
            <div className="flex gap-2">
              <Input
                value={form.category}
                onChange={set("category")}
                placeholder="Nome da nova categoria"
                data-testid="project-new-category-input"
              />
              <Button type="button" variant="outline" onClick={() => setNewCat(false)} className="shrink-0 rounded-full">
                Escolher
              </Button>
            </div>
          ) : (
            <div className="flex gap-2">
              <div className="flex-1">
                <NativeSelect
                  value={form.category_id}
                  onChange={(v) => setForm((f) => ({ ...f, category_id: v }))}
                  options={[
                    { value: "", label: "Sem categoria" },
                    ...cats.map((c) => ({ value: c.id, label: c.name })),
                  ]}
                  data-testid="project-category-select"
                />
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={() => setNewCat(true)}
                data-testid="project-new-category-btn"
                className="shrink-0 rounded-full"
              >
                Nova
              </Button>
            </div>
          )}
        </Field>
        <Field label="Descrição">
          <Textarea value={form.description} onChange={set("description")} rows={2} data-testid="project-description-input" />
        </Field>
        <Field label="Observações">
          <Textarea value={form.notes} onChange={set("notes")} rows={2} data-testid="project-notes-input" />
        </Field>
        <Button
          type="submit"
          disabled={busy}
          data-testid="save-project-btn"
          className="h-12 rounded-full font-semibold transition-transform active:scale-95"
        >
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Salvar projeto
        </Button>
      </form>
    </ResponsiveModal>
  );
}
